import React, { useEffect } from 'react';
import HeroSection from './components/HeroSection';
import SolutionsShowcase from './components/SolutionsShowcase';
import WhySTEMForge from './components/WhySTEMForge';
import AboutSection from './components/AboutSection';
import MissionVision from './components/MissionVision';
import NEPAlignment from './components/NEPAlignment';
import StudentOutcomes from './components/StudentOutcomes';
import TestimonialSection from './components/TestimonialSection';
import ContactSection from './components/ContactSection';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function HomePage() {
  useEffect(() => {
    document.title = 'STEMForge Tech Labs | STEM, AI & Robotics Education for Schools';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'STEMForge Tech Labs empowers schools with cutting-edge STEM labs, AI & Robotics programs, teacher training, and 12-month academic integration for future-ready education.');
    setMeta('keywords', 'STEM education, AI robotics lab, school STEM integration, STEMForge Tech Labs');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'STEMForge Tech Labs | STEM, AI & Robotics Education for Schools', 'property');
    setMeta('og:description', 'STEMForge Tech Labs empowers schools with cutting-edge STEM labs, AI & Robotics programs, teacher training, and 12-month academic integration.', 'property');
    setMeta('og:url', `${SITE_URL}/`, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:title', 'STEMForge Tech Labs | STEM, AI & Robotics Education for Schools');
    setMeta('twitter:description', 'STEMForge Tech Labs empowers schools with cutting-edge STEM labs, AI & Robotics programs, teacher training, and 12-month academic integration.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/`;

    // Schema.org JSON-LD
    const schemaId = 'schema-home';
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      (script as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'STEMForge Tech Labs',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/favicon.png`,
          },
          description: 'STEMForge Tech Labs empowers schools with cutting-edge STEM labs, AI & Robotics programs, teacher training, and 12-month academic integration for future-ready education.',
          sameAs: [],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: SITE_URL,
          name: 'STEMForge Tech Labs',
          publisher: { '@id': `${SITE_URL}/#organization` },
        },
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/#webpage`,
          url: `${SITE_URL}/`,
          name: 'STEMForge Tech Labs | STEM, AI & Robotics Education for Schools',
          description: 'STEMForge Tech Labs empowers schools with cutting-edge STEM labs, AI & Robotics programs, teacher training, and 12-month academic integration for future-ready education.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: { '@id': `${SITE_URL}/#organization` },
          dateModified: '2026-04-10',
        },
        {
          '@type': 'EducationalOrganization',
          '@id': `${SITE_URL}/#edu-org`,
          name: 'STEMForge Tech Labs',
          url: SITE_URL,
          description: 'Providing STEM, AI, and Robotics education solutions for schools across India.',
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'STEM Education Solutions',
            itemListElement: [
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'STEM Lab Setup' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI & Robotics Lab' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Teacher Training' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '12-Month Academic Integration' } },
            ],
          },
        },
      ],
    });

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    const sectionId = sessionStorage.getItem('scrollToSection');
    if (sectionId) {
      sessionStorage.removeItem('scrollToSection');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const offset = 80;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <HeroSection />
      <SolutionsShowcase />
      <WhySTEMForge />
      <AboutSection />
      <MissionVision />
      <NEPAlignment />
      <StudentOutcomes />
      <TestimonialSection />
      <ContactSection />
      <Footer />
    </main>
  );
}
